import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Field } from '../components/page';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ApiError, MFA_REQUIRED } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';

export function LoginPage() {
  const t = useT();
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string; sso?: string };
  const [form, setForm] = useState({ tenantCode: '', email: '', password: '', totp: '' });
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(
    search.sso && search.sso !== 'ok'
      ? (t.auth.sso[search.sso as keyof typeof t.auth.sso] ?? null)
      : null,
  );
  const [pending, setPending] = useState(false);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  /** Compte unique Beta Suite : le cabinet doit être connu pour choisir le tenant (BA-074). */
  function betaSuite() {
    const tenantCode = form.tenantCode.trim().toUpperCase();
    if (!tenantCode) return setError(t.auth.sso.tenantRequired);
    const target =
      search.redirect?.startsWith('/') && !search.redirect.startsWith('//') ? search.redirect : '/';
    const query = new URLSearchParams({ tenantCode, redirect: target });
    window.location.assign(
      `${import.meta.env.VITE_API_BASE_URL ?? ''}/v1/auth/oidc/start?${query}`,
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const session = await auth.login({
        tenantCode: form.tenantCode.trim().toUpperCase(),
        email: form.email.trim(),
        password: form.password,
        ...(needsTotp && form.totp ? { totp: form.totp } : {}),
      });
      const target =
        search.redirect?.startsWith('/') && !search.redirect.startsWith('//')
          ? search.redirect
          : '/';
      // Administrateur sans TOTP : configuration d'abord (BA-045).
      if (session.user.mfaSetupRequired)
        await navigate({ to: '/parametres', search: { tab: 'securite' } });
      else await navigate({ to: target });
    } catch (err) {
      if (err instanceof ApiError && err.type === MFA_REQUIRED) {
        if (needsTotp) setError(err.title);
        setNeedsTotp(true);
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t.auth.locked);
      } else {
        setError(
          err instanceof ApiError && err.status === 401 ? t.auth.invalid : (err as Error).message,
        );
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6">
          <h1 className="text-xl font-semibold text-primary">{t.appName}</h1>
          <p className="mb-6 text-sm text-muted-foreground">{t.auth.loginTitle}</p>
          <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
            <Field label={t.auth.tenantCode}>
              <Input
                value={form.tenantCode}
                onChange={set('tenantCode')}
                autoComplete="organization"
                required
              />
            </Field>
            <Field label={t.auth.email}>
              <Input
                type="email"
                value={form.email}
                onChange={set('email')}
                autoComplete="username"
                required
              />
            </Field>
            <Field label={t.auth.password}>
              <Input
                type="password"
                value={form.password}
                onChange={set('password')}
                autoComplete="current-password"
                required
              />
            </Field>
            {needsTotp ? (
              <Field label={t.auth.totp} hint={t.auth.totpHint}>
                <Input
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={form.totp}
                  onChange={set('totp')}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </Field>
            ) : null}
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={pending}>
              {t.auth.submit}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            {t.auth.sso.or}
            <span className="h-px flex-1 bg-border" />
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={betaSuite}>
            {t.auth.sso.button}
          </Button>
          <p className="mt-4 text-center text-sm">
            <Link to="/inscription" className="text-primary hover:underline">
              {t.signup.cta}
            </Link>
          </p>
          <p className="mt-6 text-xs text-muted-foreground">{t.common.sovereign}</p>
        </CardContent>
      </Card>
    </main>
  );
}
